import { useState, type ReactNode } from "react";

import LiveAnnouncer from "@/blueprint/components/LiveAnnouncer/LiveAnnouncer";
import SkipToContent from "@/components/Layout/SkipToContent";
import AnnouncementBar from "@/chrome/AnnouncementBar/AnnouncementBar";
import {
  ChromeOverlayToneContext,
  type ChromeOverlayTone,
} from "@/chrome/ChromeOverlayTone";
import { ChromeModeContext, type ChromeMode } from "@/chrome/ChromeMode";
import SiteFooter, { type FooterTone } from "@/chrome/SiteFooter/SiteFooter";
import PricingNoteModal from "@/components/PricingNoteModal";
import SideCart from "@/components/Cart/SideCart";
import MinimalNav from "@/chrome/MinimalNav/MinimalNav";
import SiteNav from "@/chrome/SiteNav/SiteNav";
import { cn } from "@/lib/utils";

// Chrome for every page, selected per-page via getLayout and used as _app's
// fallback. Presents the redesign chrome over the shared cart engine.
type RedesignLayoutProps = {
  children: ReactNode;
  footerTone?: FooterTone;
  chromeMode?: ChromeMode;
  /** "minimal": wordmark and cart only, for pages reached from an ad. */
  navMode?: "default" | "collapsed" | "minimal";
  showAnnouncement?: boolean;
  /** Replaces the site announcement with the page's own line. */
  announcement?: ReactNode;
  /** The footer's live counters; off where the page wants a quieter close. */
  footerStats?: boolean;
  /** Pages whose hero paints to the top edge tint the chrome band to match. */
  pageSurface?: "default" | "neutral1";
  /**
   * Padding at the page's foot that a page's fixed bars need to scroll the
   * footer clear of them, e.g. PINNED_BAR_CLEARANCE; none by default.
   */
  bottomClearance?: string;
  /** The nav scrolls away rather than floating (SiteNav). */
  navScrollsAway?: boolean;
  /** The floating nav stays hidden while this element is on screen (SiteNav). */
  navHiddenOverId?: string;
};

const RedesignLayout = ({
  children,
  footerTone,
  chromeMode = "default",
  navMode = "default",
  showAnnouncement = true,
  announcement,
  pageSurface = "default",
  footerStats = true,
  bottomClearance,
  navScrollsAway = false,
  navHiddenOverId,
}: RedesignLayoutProps) => {
  // Page content can switch chrome after mount; until it does, the prop stands.
  const [modeOverride, setChromeMode] = useState<ChromeMode | null>(null);
  const darkOverlayChrome = (modeOverride ?? chromeMode) === "dark-overlay";
  // The page's hero can flip the overlaid chrome to black text while it shows
  // light media (e.g. the shop gallery's white product stills).
  const [overlayTone, setOverlayTone] = useState<ChromeOverlayTone>("dark");
  const chromeOnDarkMedia = darkOverlayChrome && overlayTone === "dark";

  return (
    <LiveAnnouncer>
      <div
        className={cn(
          pageSurface === "neutral1" ? "bg-neutral1" : "bg-surface-page",
          bottomClearance,
        )}
      >
        <SkipToContent />
        <SideCart />
        <PricingNoteModal />
        <div className="mx-auto max-w-container">
          <div
            data-site-chrome
            className={cn(
              "bg-surface-chrome",
              darkOverlayChrome && "relative z-30 h-0 bg-transparent",
              // Ad landing pages keep their wordmark and cart in reach from
              // 1024 up, as Eight Sleep's product page does.
              // Solid once pinned: the chrome surface is translucent.
              navMode === "minimal" &&
                "wide:sticky wide:top-0 wide:z-30 wide:bg-surface-page",
            )}
          >
            {showAnnouncement && (
              <div data-theme={chromeOnDarkMedia ? "dark" : undefined}>
                {announcement ?? <AnnouncementBar />}
              </div>
            )}
            {navMode === "minimal" ? (
              <MinimalNav />
            ) : (
              <SiteNav
                mode={navMode}
                topNavTheme={chromeOnDarkMedia ? "dark" : undefined}
                scrollsAway={navScrollsAway}
                {...(navHiddenOverId === undefined
                  ? {}
                  : { hiddenOverId: navHiddenOverId })}
              />
            )}
          </div>
          <main id="main-content">
            <ChromeModeContext.Provider value={setChromeMode}>
              <ChromeOverlayToneContext.Provider value={setOverlayTone}>
                {children}
              </ChromeOverlayToneContext.Provider>
            </ChromeModeContext.Provider>
          </main>
          <SiteFooter tone={footerTone} stats={footerStats} />
        </div>
      </div>
    </LiveAnnouncer>
  );
};

export default RedesignLayout;
